import { useAnalytics } from '../../../v2/third-party-integration/amplitude';
import BaseCard from './BaseCard';
import { AMPLITUDE_EVENTS, DEVICE } from '../../../constants';
import { useMobileAppLaunchStore } from '../../home/useMobileAppLaunchStore';
import { detectDevice } from '../../../util/mobileAppUtils';
import { detectBrowser } from '../../../util/DriveUtils';
import { setDocumentTitle } from '../../../util/TitleManager';

interface Props {
  id: string;
  isEnabled: boolean;
  isFavorite?: boolean;
  description: string;
  name: string;
  iconColor: string;
  iconText: string;
  url: string;
  cdkuUrl?: string;
  onCdkUniversityClick?: Function;
  onFavoriteChange?: Function;
  playStoreUrl: string;
  appStoreUrl: string;
  isRestricted?: boolean;
  restrictedTooltipMsg?: string;
}

const BROWSER_CODES: Record<string, string> = {
  'Google Chrome': 'chrome',
  'Mozilla Firefox': 'firefox',
  'Apple Safari': 'safari',
  'Microsoft Edge': 'edge',
  'Internet Explorer': 'internetexplorer',
  Opera: 'opera',
};

export default function MobileApplicationCard({
  id,
  isFavorite,
  isEnabled,
  description,
  name,
  iconColor,
  iconText,
  cdkuUrl,
  onCdkUniversityClick,
  onFavoriteChange,
  url,
  playStoreUrl = '',
  appStoreUrl = '',
  isRestricted,
  restrictedTooltipMsg,
}: Props) {
  const { trackEvent } = useAnalytics();

  const setShowDialog = useMobileAppLaunchStore((state: any) => state.setShowDialog);
  const setAppName = useMobileAppLaunchStore((state: any) => state.setAppName);
  const setAppIcon = useMobileAppLaunchStore((state: any) => state.setAppIcon);
  const setAppIconColor = useMobileAppLaunchStore((state: any) => state.setAppIconColor);
  const setAppDescription = useMobileAppLaunchStore((state: any) => state.setAppDescription);
  const setAppDownloadUrl = useMobileAppLaunchStore((state: any) => state.setAppDownloadUrl);

  const device = detectDevice();
  const browserCode = BROWSER_CODES[detectBrowser()] || '';

  const handleOpenMobileApp = async () => {
    setDocumentTitle(name, url);
    trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, { Application: name });

    const launchUrl = browserCode ? `${url}&browser=${browserCode}` : url;
    const timeOut = browserCode === 'safari' ? 4000 : 2000;
    let appOpened = false;

    const checkDeepLink = setTimeout(() => {
      if (!appOpened) {
        setShowDialog(true);
        setAppName(name);
        setAppIcon(iconText);
        setAppIconColor(iconColor);
        setAppDescription(description);
        setAppDownloadUrl(device === DEVICE.IOS ? appStoreUrl : playStoreUrl);
      }
    }, timeOut);

    const onVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
        clearTimeout(checkDeepLink);
        setShowDialog(false);
      }
    };

    window.addEventListener('visibilitychange', onVisibilityChange);
    window.location.href = launchUrl;

    setTimeout(() => {
      window.removeEventListener('visibilitychange', onVisibilityChange);
    }, 6000);
  };

  return (
    <BaseCard
      id={id}
      isIconRequired={true}
      isFavorite={isFavorite}
      isEnabled={true}
      name={name}
      iconText={iconText}
      iconColor={iconColor}
      learnMoreDescription={description}
      cdkuUrl={cdkuUrl}
      url={url}
      onClick={handleOpenMobileApp}
      onCdkUniversityClick={onCdkUniversityClick}
      onFavoriteChange={onFavoriteChange}
      isMobileApp={isEnabled}
      isRestricted={isRestricted}
      restrictedTooltipMsg={restrictedTooltipMsg}
    />
  );
}
